import {
  Building,
  FileText,
  Info,
  Mail,
  MapPin,
  Phone,
  Shield,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { useGetMyStudyParticipantsMeStudyGet } from "@/api-v3/client/participants/participants";
import { CardContainer } from "@/components/card-container";
import { ErrorCard } from "@/components/error-card";
import { InfoItem, SectionTitle } from "@/components/info-item";
import { Skeleton } from "@/components/ui/skeleton";
import StorageManager from "@/features/storage";
import { parseError } from "@/lib/error-handler";
import { formatKeyText } from "@/lib/utils";

const SkeletonInfoItem = () => (
  <div className="flex items-start mb-4 p-3 rounded-lg">
    <Skeleton className="mr-2 mt-1 h-[18px] w-[18px] rounded" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-4 w-40" />
    </div>
  </div>
);

const TrialDetailsSkeleton = () => (
  <div role="status" aria-label="Loading trial details">
    <section className="mb-8">
      <Skeleton className="h-5 w-40 mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        <div>
          <SkeletonInfoItem />
        </div>
      </div>
    </section>

    <section className="mb-8">
      <Skeleton className="h-5 w-40 mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        <div>
          <SkeletonInfoItem />
          <SkeletonInfoItem />
          <SkeletonInfoItem />
        </div>
        <div>
          <SkeletonInfoItem />
          <SkeletonInfoItem />
          <SkeletonInfoItem />
        </div>
      </div>
    </section>

    <section>
      <Skeleton className="h-5 w-40 mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        <div>
          <SkeletonInfoItem />
          <SkeletonInfoItem />
        </div>
        <div>
          <SkeletonInfoItem />
        </div>
      </div>
    </section>
  </div>
);

export const TrialDetailsPage = () => {
  const { t } = useTranslation();

  const { data, isLoading, error, refetch } =
    useGetMyStudyParticipantsMeStudyGet();

  const loginId = StorageManager.getLoginInformation()?.loginId;

  if (error) {
    const { title, message } = parseError(error);
    return (
      <ErrorCard
        title={t(title)}
        message={t(message)}
        buttonLabel={t("retry")}
        onButtonClick={() => refetch()}
      />
    );
  }

  return (
    <CardContainer isLoading={isLoading}>
      <CardContainer.Header>
        <CardContainer.HeaderContent>
          <CardContainer.CardTitle>
            {t("visits_trialTitle")}
          </CardContainer.CardTitle>
          <CardContainer.CardDescription>
            {t("visits_trialSubtitle")}
          </CardContainer.CardDescription>
        </CardContainer.HeaderContent>
      </CardContainer.Header>
      <CardContainer.Content>
        {isLoading ? (
          <TrialDetailsSkeleton />
        ) : (
          <>
            <section className="mb-8">
              <SectionTitle>
                {t("trialDetails_section_participantInformation")}
              </SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div>
                  <InfoItem
                    icon={<User size={18} />}
                    label={t("login_id")}
                    value={loginId}
                  />
                </div>
              </div>
            </section>

            <section className="mb-8">
              <SectionTitle>
                {t("trialDetails_section_studyInformation")}
              </SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div>
                  <InfoItem
                    icon={<Info size={18} />}
                    label={t("trialDetails_studyTitle")}
                    value={data?.study_name}
                  />
                  <InfoItem
                    icon={<FileText size={18} />}
                    label={t("trialDetails_studyNumber")}
                    value={data?.study_number}
                  />
                  <InfoItem
                    icon={<Building size={18} />}
                    label={t("trialDetails_sponsor")}
                    value={data?.sponsor}
                  />
                </div>
                <div>
                  <InfoItem
                    icon={<User size={18} />}
                    label={t("trialDetails_principalInvestigator")}
                    value={data?.principal_investigator || "N/A"}
                  />
                  {data?.phase && (
                    <InfoItem
                      icon={<Shield size={18} />}
                      label={t("trialDetails_phase")}
                      value={formatKeyText(data.phase)}
                    />
                  )}
                  <InfoItem
                    icon={<MapPin size={18} />}
                    label={t("trialDetails_location")}
                    value={data?.location}
                  />
                </div>
              </div>
            </section>

            <section>
              <SectionTitle>
                {t("trialDetails_section_primaryContact")}
              </SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div>
                  <InfoItem
                    icon={<User size={18} />}
                    label={t("trialDetails_primaryContactName")}
                    value={data?.primary_contact_name}
                  />
                  <InfoItem
                    icon={<Phone size={18} />}
                    label={t("trialDetails_primaryContactPhone")}
                    value={data?.primary_contact_phone}
                  />
                </div>
                <div>
                  <InfoItem
                    icon={<Mail size={18} />}
                    label={t("trialDetails_primaryContactEmail")}
                    value={data?.primary_contact_email}
                  />
                </div>
              </div>
            </section>
          </>
        )}
      </CardContainer.Content>
    </CardContainer>
  );
};
